// Shared types + pure helpers for the global search UI. The record/cluster
// shapes mirror the server envelope (docs/architecture/unified-search/api.md);
// the helpers (highlight segmentation, Recent push, view flattening) are pure so
// they unit-test in isolation (search-helpers.test.ts).

// Searchable-as-header: owner, dog, helper, user. `booking` + `leave` are
// context-only members (a stay under an owner/dog; a leave entry under a helper).
export type SearchType = 'owner' | 'dog' | 'booking' | 'helper' | 'user' | 'leave';
// Scope = the matchable types only (no booking/leave — you can't search for them).
export type SearchScope = 'all' | 'owner' | 'dog' | 'helper' | 'user';

// Below this many characters we don't search (the panel shows a hint). The real
// safeguard against a broad "a" query — server enforces the same bound.
export const MIN_QUERY_LENGTH = 3;

export interface SearchRecord {
  type: SearchType;
  id: string;
  name: string;
  sub: string;
  matched: boolean;
  status?: string | null;
  // dog rows only — the owning owner_id, so a dog-click opens its household.
  householdId?: string;
}

export interface SearchCluster {
  header: SearchRecord;
  members: SearchRecord[];
  // Stays beyond the nested cap — rendered as a "+N more" line, not extra rows.
  moreBookings?: number;
}

export interface SearchResult {
  clusters: SearchCluster[];
  matchCount: number;
  capped: boolean;
}

// type → the row's icon (real bbbk Icon names) + chip label. Colours are NOT
// here — they live in search.css keyed on the row's `data-type`, so a type's
// colour is defined once (single source) and the JSX carries no style props.
// Every type gets a DISTINCT glyph: owner=users (a household), dog=paw,
// booking=calendar, leave=calendar-today, helper=clock, user=user.
export const TYPE_META: Record<SearchType, { icon: string; label: string }> = {
  owner: { icon: 'users', label: 'Owner' },
  dog: { icon: 'paw', label: 'Dog' },
  booking: { icon: 'calendar', label: 'Booking' },
  leave: { icon: 'calendar-today', label: 'Leave' },
  helper: { icon: 'clock', label: 'Helper' },
  user: { icon: 'user', label: 'User' },
};

// The scope segmented tabs, in order. No Bookings tab — bookings aren't
// matchable. `user` is admin-only — filtered out at render for a non-admin.
export const SCOPES: { key: SearchScope; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'owner', label: 'Owners' },
  { key: 'dog', label: 'Dogs' },
  { key: 'helper', label: 'Helpers' },
  { key: 'user', label: 'Users' },
];

// A stable identity for a record — unique across the result set (type + id).
export const recordKey = (r: SearchRecord): string => `${r.type}:${r.id}`;

// Split a display string into segments, marking every case-insensitive
// occurrence of `q` — so a hit reads no matter which field it landed in (name,
// breed, phone, email, dates). Pure; the row component wraps `mark:true`
// segments in <mark>. Empty q → the whole string as one unmarked segment.
export interface HL {
  text: string;
  mark: boolean;
}
export function splitHighlight(text: string, q: string): HL[] {
  // Defensive: a record can carry a null name/sub from the DB (an owner with no
  // contact name, a helper with no role) — never crash the panel over it.
  const safe = text ?? '';
  const needle = q.trim().toLowerCase();
  if (needle === '') return [{ text: safe, mark: false }];
  const lower = safe.toLowerCase();
  const out: HL[] = [];
  let i = 0;
  let idx = lower.indexOf(needle, i);
  while (idx !== -1) {
    if (idx > i) out.push({ text: safe.slice(i, idx), mark: false });
    out.push({ text: safe.slice(idx, idx + needle.length), mark: true });
    i = idx + needle.length;
    idx = lower.indexOf(needle, i);
  }
  if (i < safe.length) out.push({ text: safe.slice(i), mark: false });
  return out;
}

// Prepend a just-opened cluster to Recent, de-duped by its header identity (an
// already-present household floats to the top rather than doubling), capped.
// Recent carries clusters (not bare records) so a recent owner shows its dogs
// and a recent dog shows its owner — the same context as a live result.
export function pushRecentCluster(
  list: SearchCluster[],
  cluster: SearchCluster,
  cap: number,
): SearchCluster[] {
  const key = recordKey(cluster.header);
  return [cluster, ...list.filter((c) => recordKey(c.header) !== key)].slice(0, cap);
}

// Header-then-members, every cluster, in render order.
function flattenClusters(clusters: SearchCluster[]): SearchRecord[] {
  const out: SearchRecord[] = [];
  for (const c of clusters) {
    out.push(c.header);
    out.push(...c.members);
  }
  return out;
}

// The openable rows of the current panel view, in the exact visual order they
// render — so keyboard nav (↑/↓/Enter) can index a flat list that matches what
// the eye sees. No query → Recent clusters; else → the result clusters. Shared by
// the panel (renders) and the keyboard handler (indexes), so the two never drift.
export function flattenView(
  hasQuery: boolean,
  result: SearchResult | null,
  recent: SearchCluster[],
): SearchRecord[] {
  if (!hasQuery) return flattenClusters(recent);
  if (!result) return [];
  return flattenClusters(result.clusters);
}
